/**
 * Formateo de fechas/horas centralizado.
 *
 * Dos categorías que NO deben mezclarse:
 *
 * 1. Fechas-solo ("YYYY-MM-DD"): start_date, end_date, work_date, etc. Son
 *    fechas de calendario sin hora — el 1 de julio es el 1 de julio en todo
 *    el mundo. Se parsean con componentes LOCALES (nunca `new Date("YYYY-MM-DD")`,
 *    que JS interpreta como medianoche UTC y desfasa el día). El resultado es
 *    independiente de zona horaria.
 *
 * 2. Instantes (ISO con hora): clock_in_at, requested_at, decided_at, etc.
 *    Su hora de pared depende de la zona. SIEMPRE se formatean en la zona del
 *    workspace (`tz`), no en la del navegador. La tz sale de
 *    useCurrentMembership().timezone (o `data.timezone` de endpoints que la traen).
 */

export const DEFAULT_TZ = "UTC";

const MESES_ES = [
  "enero", "febrero", "marzo", "abril", "mayo", "junio",
  "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre",
];

// ---------------------------------------------------------------------------
// Fechas-solo (YYYY-MM-DD) — independientes de zona horaria
// ---------------------------------------------------------------------------

/** Parsea "YYYY-MM-DD" como fecha LOCAL (sin desfase UTC). */
export function ymdToLocalDate(ymd) {
  const [y, m, d] = String(ymd).split("-").map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
}

/** Convierte una Date a "YYYY-MM-DD" usando componentes locales. */
export function localDateToYmd(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

/** "YYYY-MM-DD" de hoy en la zona local del navegador. */
export function todayYmd() {
  return localDateToYmd(new Date());
}

/**
 * Formatea una fecha-solo ("YYYY-MM-DD") para mostrar. Independiente de tz.
 * @param {string} ymd
 * @param {Intl.DateTimeFormatOptions} [opts] por defecto: día numérico + mes corto + año
 */
export function formatDateOnly(ymd, opts) {
  if (!ymd) return "—";
  const date = ymdToLocalDate(ymd);
  if (!date) return ymd;
  return new Intl.DateTimeFormat("es-ES", {
    day: "2-digit", month: "short", year: "numeric", ...opts,
  }).format(date);
}

/** Rango de fechas-solo: "1 jul 2026" o "1 jul 2026 → 3 jul 2026". */
export function formatDateRange(startYmd, endYmd) {
  if (!startYmd) return "—";
  const fmt = (ymd) => {
    const [y, m, d] = String(ymd).split("-").map(Number);
    if (!y || !m || !d) return ymd;
    return `${d} ${MESES_ES[m - 1]} ${y}`;
  };
  if (!endYmd || endYmd === startYmd) return fmt(startYmd);
  return `${fmt(startYmd)} → ${fmt(endYmd)}`;
}

// ---------------------------------------------------------------------------
// Instantes (ISO con hora) — SIEMPRE en la zona del workspace
// ---------------------------------------------------------------------------

/**
 * Hora "HH:MM" de un instante ISO, en la zona del workspace.
 * @param {string} iso
 * @param {string} tz IANA timezone (workspace). Default DEFAULT_TZ.
 */
export function formatTimeHHMM(iso, tz = DEFAULT_TZ) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      timeZone: tz, hour: "2-digit", minute: "2-digit", hour12: false,
    }).format(new Date(iso));
  } catch {
    return "—";
  }
}

/**
 * Fecha + hora de un instante ISO, en la zona del workspace.
 * @param {string} iso
 * @param {string} tz IANA timezone (workspace). Default DEFAULT_TZ.
 * @param {Intl.DateTimeFormatOptions} [opts] sobrescribe el preset
 */
export function formatInstant(iso, tz = DEFAULT_TZ, opts) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("es-ES", {
      timeZone: tz,
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
      ...opts,
    }).format(new Date(iso));
  } catch {
    return "—";
  }
}

/** "YYYY-MM-DD" de hoy en la zona del workspace (para comparar con fechas-solo). */
export function todayYmdInTz(tz = DEFAULT_TZ) {
  try {
    // en-CA da formato ISO YYYY-MM-DD
    return new Intl.DateTimeFormat("en-CA", {
      timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    }).format(new Date());
  } catch {
    return todayYmd();
  }
}
